import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, CheckCircle2, AlertCircle, X, BellPlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { enablePush, pushSupported, pushPermission } from "@/lib/push";

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
};

const Icon = ({ type }) => {
  if (type === "success") return <CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" />;
  if (type === "warning") return <AlertCircle size={16} className="text-yellow-500 mt-0.5 shrink-0" />;
  if (type === "event_reminder") return <BellRing size={16} className="text-primary mt-0.5 shrink-0" />;
  return <Bell size={16} className="text-muted-foreground mt-0.5 shrink-0" />;
};

export default function NotificationBell({ variant = "" }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [perm, setPerm] = useState(pushPermission());
  const ref = useRef(null);

  const unread = items.filter((n) => !n.read).length;

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  const activate = async () => {
    setEnabling(true);
    try {
      await enablePush();
      setPerm("granted");
      await api.post("/push/test").catch(() => {});
      toast.success("Notifications activées sur cet appareil.");
    } catch (e) {
      if (e.message === "denied") toast.error("Notifications refusées. Autorisez-les dans les réglages du navigateur.");
      else if (e.message === "unsupported") toast.error("Votre navigateur ne supporte pas les notifications push.");
      else toast.error("Impossible d'activer les notifications.");
    } finally {
      setEnabling(false);
    }
  };

  const onOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && unread) markAll();
  };

  const clickItem = (n) => {
    setOpen(false);
    if (n.type === "event_reminder" || n.event_id) navigate("/planning");
  };

  const showActivate = pushSupported() && perm !== "granted";
  const sfx = variant ? `-${variant}` : "";

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid={`notif-bell-btn${sfx}`}
        onClick={onOpen}
        aria-label="Notifications"
        className="relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span
            data-testid={`notif-bell-count${sfx}`}
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid={`notif-panel${sfx}`}
          className={`absolute ${variant === "desktop" ? "left-0" : "right-0"} mt-2 w-[320px] max-w-[calc(100vw-2rem)] rounded-2xl border border-border bg-card shadow-2xl z-[70] overflow-hidden`}
        >
          <div className="flex items-center justify-between px-4 h-12 border-b border-border">
            <span className="font-display font-bold uppercase tracking-tight text-sm flex items-center gap-2">
              <BellRing size={16} className="text-primary" /> Notifications
            </span>
            <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground p-1">
              <X size={16} />
            </button>
          </div>

          {showActivate && (
            <button
              data-testid={`notif-enable-push-btn${sfx}`}
              onClick={activate}
              disabled={enabling}
              className="w-full flex items-center gap-2 px-4 py-3 bg-primary/10 text-primary text-xs font-semibold border-b border-border hover:bg-primary/15 transition-colors disabled:opacity-60"
            >
              <BellPlus size={16} />
              {enabling ? "Activation..." : "Activer les notifications sur cet appareil"}
            </button>
          )}

          <div className="max-h-[60vh] overflow-y-auto divide-y divide-border">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notif-empty">
                Aucune notification.
              </p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  data-testid="notif-item"
                  onClick={() => clickItem(n)}
                  className={`w-full text-left flex items-start gap-2.5 px-4 py-3 hover:bg-secondary/60 transition-colors ${!n.read ? "bg-primary/5" : ""}`}
                >
                  <Icon type={n.type} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold leading-tight">{n.title}</p>
                    {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground/60 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
